import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';

import { exportRefusals, listRefusals, summarizeRefusals } from '../api/refusals';

import type { RequestFilters } from '../model/filters';

/**
 * Server state for the viewer.
 *
 * Every read is recorded upstream as an audit access, and an employee may make
 * 30 a minute, so nothing here reads on its own: no retry, no refetch on focus,
 * and 30 s before an identical query is read again. A failure is shown with a
 * "Try again" the employee presses.
 *
 * The key holds the parsed filters, never the raw URL, so two links that mean
 * the same thing share one cache entry.
 */
export const refusalKeys = {
    all: ['unidentified-refusals'] as const,
    list: (filters: RequestFilters) => [...refusalKeys.all, 'list', filters] as const,
    summary: (filters: RequestFilters) => [...refusalKeys.all, 'summary', filters] as const,
};

const READ_POLICY = {
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 30_000,
} as const;

export function useRefusals(filters: RequestFilters) {
    return useInfiniteQuery({
        queryKey: refusalKeys.list(filters),
        queryFn: ({ pageParam, signal }) => listRefusals(filters, pageParam, signal),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: (page) => page.nextCursor,
        ...READ_POLICY,
    });
}

export function useRefusalSummary(filters: RequestFilters) {
    return useQuery({
        queryKey: refusalKeys.summary(filters),
        queryFn: ({ signal }) => summarizeRefusals(filters, signal),
        ...READ_POLICY,
    });
}

/** The CSV, handed to the browser as a download under the server's file name. */
export function useExportRefusals() {
    return useMutation({
        mutationFn: async (filters: RequestFilters) => {
            const file = await exportRefusals(filters);
            saveFile(file.blob, file.fileName ?? 'unidentified-refusals.csv');
        },
    });
}

function saveFile(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    // Revoked on the next tick: some browsers start the download asynchronously.
    setTimeout(() => {
        URL.revokeObjectURL(url);
    }, 0);
}
